import { Box } from "@mui/material";
import { TkSalaryAdjustmentModalProps } from "../../../type/componentsType/TkSalaryAdjustmentModal";
import { TkSelectForm } from "../../atoms/TkSelectForm";
import { useEffect, useState } from "react";
import { changeAmountFormat } from "../../../utils/useTools";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import "./_style.scss";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import TkAmountInput from "../TkAmountInput";
/**
 * TkSalaryAdjustmentModal部品は、俸給の調整額を登録するためのダイアログです。
 * @param {string} title - ダイアログのタイトル。
 * @param {boolean} open - ダイアログの表示状態。
 * @param {TkSelectProps[]} selectPropsList - 手当選択のプロパティ。
 * @param {any} rest - その他のプロパティ。
 * @return TkSalaryAdjustmentModal部品
 **/
export const TkSalaryAdjustmentModal: React.FC<TkSalaryAdjustmentModalProps> = (
  props
) => {
  const {
    title = "【俸給の調整額】",
    selectPropsList,
    onSave,
    onClose,
    showEachAmount = true,
    wantToSetValue,
    selectedBtn,
    selectValues,
    setSelectValues,
    inputValues,
    setInputValues,
    total,
    setTotal,

    ...rest
  } = props;

  const [sameSelectErrorMessage, setSameSelectErrorMessage] = useState<{
    [key: number]: string;
  }>({});
  const [statementSaveButtonDisabledBool, setStatementSaveButtonDisabledBool] =
    useState<boolean>(false);
  const [totalSaveButtonDisabledBool, setTotalSaveButtonDisabledBool] =
    useState<boolean>(false);

  useEffect(() => {
    if (selectedBtn === "STATEMENT") {
      //明細ボタン押下時
      // 両方とも空オブジェクトの場合
      if (
        Object.keys(selectValues).length === 0 &&
        Object.keys(inputValues).length === 0
      ) {
        setStatementSaveButtonDisabledBool(false);
        return;
      }

      // キーの数が異なればfalseを返す
      if (
        Object.keys(selectValues).length !== Object.keys(inputValues).length
      ) {
        setStatementSaveButtonDisabledBool(true);
        return;
      }

      // 同じキーを持っているか確認
      for (let key in selectValues) {
        if (
          selectValues.hasOwnProperty(key) &&
          inputValues.hasOwnProperty(key)
        ) {
          if (Object.values(inputValues).includes(undefined!)) {
            //金額にundefindがあれば（登録ボタン：非活性）
            setStatementSaveButtonDisabledBool(true);
          } else {
            // 同じキーがあればtrueを返す
            setStatementSaveButtonDisabledBool(false);
            return;
          }
        }
      }

      // 同じキーがない場合はfalse
      setStatementSaveButtonDisabledBool(true);
    } else {
      if (Object.keys(selectValues).length > 0 && total > 0) {
        //どっちも入力している（活性）
        setTotalSaveButtonDisabledBool(false);
      } else if (Object.keys(selectValues).length === 0 && total > 0) {
        //手当未選択で合計金額入力されている場合（非活性）
        setTotalSaveButtonDisabledBool(true);
      } else if (Object.keys(selectValues).length === 0 && total === 0) {
        //どっちも未入力の場合（活性）
        setTotalSaveButtonDisabledBool(false);
      } else if (Object.keys(selectValues).length > 0 && total === 0) {
        //手当選択済、0円の場合（非活性）
        setTotalSaveButtonDisabledBool(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectValues, inputValues, total]);

  useEffect(() => {
    // 手当未選択の場合エラーメッセージのクリア処理を行う
    const updatedMassege = { ...sameSelectErrorMessage };
    Object.keys(sameSelectErrorMessage).forEach((key) => {
      if (!(key in selectValues)) {
        updatedMassege[Number(key)] = "";
      }
    });
    setSameSelectErrorMessage(updatedMassege);

    // 同じ手当を選択した場合はエラーを表示する
    let tempArr = Object.entries(selectValues);
    tempArr.forEach(([key, value]) => {
      if (!value) {
        setSameSelectErrorMessage((prev: any) => {
          return { ...prev, [key]: "" };
        });
        return;
      }

      const length = tempArr.filter(([_, v]) => v === value).length;

      if (length > 1) {
        setSameSelectErrorMessage((prev: any) => {
          return { ...prev, [key]: TkPropertyMessageGet("TKMSG_SCR0071E") };
        });
      } else {
        setSameSelectErrorMessage((prev: any) => {
          return { ...prev, [key]: "" };
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectValues]);

  useEffect(() => {
    // 金額の合計を計算する
    if (!showEachAmount) {
      return;
    }
    if (Object.keys(inputValues).length > 0) {
      if (Object.values(inputValues).includes(undefined!)) {
        const newInputValues = { ...inputValues };
        for (let key in newInputValues) {
          if (newInputValues[key] === undefined) {
            delete newInputValues[key];
          }
        }
        const total = Object.values(newInputValues).reduce(
          (prev: number, current: number) => Number(prev) + Number(current),
          0
        );
        setTotal(total);
      } else {
        const total = Object.values(inputValues).reduce(
          (prev: number, current: number) => Number(prev) + Number(current),
          0
        );
        setTotal(total);
      }
    } else {
      setTotal(wantToSetValue?.total);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValues, showEachAmount]);

  // 手当選択の値が変更されたときのイベント
  const handleGetValue = (index: number) => (value: string) => {
    if (value === "") {
      const newSelectValues = { ...selectValues };
      delete newSelectValues[index];
      setSelectValues(newSelectValues);
    } else {
      setSelectValues({ ...selectValues, [index]: value });
    }
  };

  // 金額の値が変更されたときのイベント
  const handleInputChange = (index: number) => (event: any) => {
    setInputValues((prev: any) => {
      return { ...prev, [index]: event.target.value };
    });
    if (selectedBtn === "TOTAL") {
      setTotal(event.target.value);
    }
  };

  // フォーカスアウト
  const handleBlur = (e: any, index?: any) => {
    if (e.target.value === "0" || !e.target.value) {
      if (index !== undefined) {
        const newInputValues = { ...inputValues };
        delete newInputValues[index];
        setInputValues(newInputValues);
        const newSelectValues = { ...selectValues };
        delete newSelectValues[index];
        setSelectValues(newSelectValues);
      } else {
        setInputValues({ 0: e.target.value });
        setSelectValues({});
        setTotal(e.target.value);
      }
    }
  };

  const handleTotalInputChange = (event: any) => {
    setTotal(event.target.value);
    setInputValues({ 0: event.target.value });
  };

  const handleSave = () => {
    onSave &&
      onSave({
        total,
        inputValues,
        selectValues,
      });
  };

  const clearData = () => {
    setSelectValues({});
    setInputValues({});
    setSameSelectErrorMessage([]);
  };

  return (
    <TkModalWithTitle
      title={title}
      className="tkSalaryAdjustmentModal"
      saveButtonDisabled={
        Object.values(sameSelectErrorMessage).some((value) => value !== "") ||
        (selectedBtn === "STATEMENT"
          ? statementSaveButtonDisabledBool
          : totalSaveButtonDisabledBool)
      }
      saveButtonName="登録"
      onSave={handleSave}
      clearSaveData={clearData}
      onClose={() => {
        onClose && onClose();
      }}
      {...rest}
    >
      <Box className="table">
        <Box className="tableHeader">
          <Box className="nameField"></Box>
          <Box className="selectField">手当選択</Box>
          <Box className="amountField">金額</Box>
        </Box>
        <Box className="tableBody">
          {Array.from({ length: 5 }, (_, index) => {
            return (
              <Box className="tableBodyField" key={index}>
                <Box className="nameField">手当{index + 1}</Box>
                <Box className="selectField">
                  <TkSelectForm
                    error={!!sameSelectErrorMessage[index]}
                    errorMessage={sameSelectErrorMessage[index]}
                    onGetValue={handleGetValue(index)}
                    selectPropsList={selectPropsList}
                    wantToSetValue={selectValues[index]}
                  />
                </Box>
                {showEachAmount && (
                  <Box className="amountField">
                    <TkAmountInput
                      wantToSetValue={inputValues[index]}
                      onChange={handleInputChange(index)}
                      onBlur={(e: any) => handleBlur(e, index)}
                      size="medium"
                    />
                  </Box>
                )}
              </Box>
            );
          })}
          <Box
            className={`tableTotal ${
              !showEachAmount && "totalAfterHideEachAmount"
            }`}
          >
            <Box className="totalField">
              <Box className="totalLabel">合計</Box>
              {showEachAmount ? (
                <Box className="totalAmount">
                  {changeAmountFormat(total) === "NaN"
                    ? ""
                    : changeAmountFormat(total)}{" "}
                  円
                </Box>
              ) : (
                <Box className="totalAmountInput">
                  <TkAmountInput
                    onChange={handleTotalInputChange}
                    wantToSetValue={wantToSetValue?.total}
                    onBlur={(e: any) => handleBlur(e)}
                    size="medium"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </TkModalWithTitle>
  );
};
export default TkSalaryAdjustmentModal;
